<div class="min-h-screen bg-slate-900">
  <!-- SECTION 1: HERO -->
  <section id="hero" class="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-mesh">
    <!-- Animated background elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-float"></div>
      <div class="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary-600/8 rounded-full blur-3xl animate-float-delayed">
      </div>
      <div class="absolute top-1/2 right-1/3 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl animate-float-slow"></div>
    </div>

    <div class="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
      <h1 class="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6 animate-fade-in-up">
        Naybrs
      </h1>
      <p class="text-xl sm:text-2xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed animate-fade-in-up-delay-1">
        A commerce platform that makes physical-world inventory discoverable and purchasable. Sellers list products,
        accept payments, and optionally appear live on a map. AI handles the operations.
      </p>
      <div class="space-y-3 mb-10 animate-fade-in-up-delay-2">
        <p class="text-slate-400">Intelligence runs the platform. Not headcount.</p>
      </div>
      <div class="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up-delay-3">
        <a href="https://demo.naybrs.com" target="_blank" rel="noopener"
          class="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 hover:-translate-y-0.5">
          View product
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </a>
        <a href="mailto:a@naybrs.com"
          class="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-slate-300 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl transition-all duration-300">
          Contact
        </a>
      </div>

      <!-- NVIDIA Inception Badge -->
      <div class="mt-12 animate-fade-in-up-delay-3">
        <a href="https://www.nvidia.com/en-us/startups/" target="_blank" rel="noopener"
          class="inline-block opacity-70 hover:opacity-100 transition-opacity duration-300"
          title="Naybrs is a member of the NVIDIA Inception program">
          <img src="assets/images/partners/nvidia-inception-badge.svg" alt="NVIDIA Inception Program member badge"
            class="h-14 sm:h-16" />
        </a>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-slate-500" fill="none" viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </div>
  </section>

  <!-- SECTION 2: THE PROBLEM -->
  <section id="problem" class="scroll-mt-header py-24 lg:py-32 bg-slate-950">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-8">The Core Problem</h2>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">
        Buying from physical sellers requires effort before certainty.
      </p>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">
        Buyers can't see what's actually for sale nearby before they show up. They guess, browse blindly, or default to
        large online marketplaces that provide certainty.
      </p>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">
        Sellers rely on foot traffic, lose demand they never see, and reset to zero after each day or event.
      </p>
      <p class="text-lg text-slate-400 leading-relaxed">
        This causes missed sales, wasted time, and demand leaking away from physical sellers by default.
      </p>
    </div>
  </section>

  <!-- SECTION 3: THE SOLUTION -->
  <section id="product" class="scroll-mt-header py-24 lg:py-32 bg-slate-900">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-8">The Solution</h2>
      <p class="text-lg text-slate-400 leading-relaxed mb-8">
        Naybrs turns physical presence into persistent commerce. AI runs the operations that would normally require a
        team.
      </p>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">Sellers:</p>
      <ul class="space-y-4 mb-8">
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Point a camera at products to create listings in seconds</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Accept credit cards and cryptocurrency with automatic order
            management</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Get data-driven pricing guidance, demand forecasts, and restock
            alerts</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Discover upcoming events — markets, festivals, and conventions — and
            declare attendance so customers know where to find you</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Choose fulfillment: pickup, delivery, or shipping</span>
        </li>
      </ul>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">
        Optionally, sellers share their live location to increase discovery while physically present.
      </p>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">Buyers:</p>
      <ul class="space-y-4 mb-8">
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">See a personalized feed of what's available nearby right now</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Browse upcoming events nearby, see which sellers will be there, and know
            what's for sale before showing up</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Tell an AI agent what they want — it searches, adds to cart, and
            completes the purchase</span>
        </li>
        <li class="flex items-start gap-3">
          <span class="mt-2 w-2 h-2 rounded-full bg-primary-500 shrink-0"></span>
          <span class="text-lg text-slate-400">Purchase with certainty, reorder from sellers they've met</span>
        </li>
      </ul>
      <div class="glass-card rounded-xl p-6 border-l-4 border-l-primary-500">
        <p class="text-lg text-slate-300 font-medium">
          When location is turned off, listings remain active and purchasable.
        </p>
        <p class="text-slate-400 mt-3">
          Standard e-commerce, with optional live presence.
        </p>
      </div>
    </div>
  </section>

  <!-- SECTION 4: HOW AI RUNS THE MARKETPLACE -->
  <section id="intelligence" class="scroll-mt-header py-24 lg:py-32 bg-slate-950">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">How Intelligence Runs the Platform</h2>
      <p class="text-lg text-slate-400 leading-relaxed mb-12">
        Every interaction on Naybrs is shaped by AI that learns from location, transactions, and behavior. These aren't
        features bolted on. They're how the platform operates.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Card: Camera Listing -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Camera-to-Listing</h3>
          </div>
          <p class="text-slate-400">
            Sellers photograph products. AI generates the title, description, category, and a suggested price. One tap
            to publish. Under 30 seconds from photo to live listing.
          </p>
        </div>

        <!-- Card: Discovery Feed -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Personalized Discovery</h3>
          </div>
          <p class="text-slate-400">
            Every buyer sees a different feed based on their location, time of day, browsing history, and what's
            trending nearby. The experience reshapes itself in real time.
          </p>
        </div>

        <!-- Card: Seller Intelligence -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Seller Intelligence</h3>
          </div>
          <p class="text-slate-400">
            Every seller gets demand forecasts, pricing guidance, quality scores, and location recommendations. Data
            that typically requires a dedicated analyst, computed daily and delivered automatically.
          </p>
        </div>

        <!-- Card: Conversational Commerce -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">AI Agents</h3>
          </div>
          <p class="text-slate-400">
            Buyers and sellers interact through autonomous AI agents that execute real operations. "Find me a leather
            bag under $60" — the agent searches, shows results, adds to cart, and completes the purchase. Sellers create
            listings, manage orders, and go live at markets by voice or text.
          </p>
        </div>

        <!-- Card: Location Intelligence -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Location Intelligence</h3>
          </div>
          <p class="text-slate-400">
            AI tells sellers where and when to sell, based on foot traffic predictions, demand patterns, and sales data
            from across the platform. Data nobody else has.
          </p>
        </div>

        <!-- Card: Event Intelligence -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Event Intelligence</h3>
          </div>
          <p class="text-slate-400">
            The platform ingests events from federal databases, ticketing platforms, and public calendars daily. AI
            scores each event against a seller's products, location, and history, then surfaces personalized
            suggestions. Sellers stop guessing which events to attend.
          </p>
        </div>

        <!-- Card: Autonomous Operations -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Self-Governing Platform</h3>
          </div>
          <p class="text-slate-400">
            Fraud detection on every transaction. Anomaly detection every five minutes with automated response. Listing
            quality scored automatically. The platform monitors and governs itself.
          </p>
        </div>

        <!-- Card: Visual Search -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Visual Search</h3>
          </div>
          <p class="text-slate-400">
            Snap a photo of something you like. AI finds visually similar products from real, nearby inventory. Every
            product image is automatically indexed with vision embeddings.
          </p>
        </div>

        <!-- Card: AI Seller Coach -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">AI Seller Coach</h3>
          </div>
          <p class="text-slate-400">
            Every seller gets a personal AI coach that reads their sales data, quality scores, and market trends, then
            delivers prioritized guidance. At-risk sellers get proactive weekly nudges.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- SECTION 5: WHAT MAKES THIS DIFFERENT -->
  <section id="different" class="scroll-mt-header py-24 lg:py-32 bg-slate-900">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-8">What Makes This Different</h2>
      <div class="space-y-8">
        <div class="glass-card rounded-xl p-6">
          <h3 class="text-xl font-semibold text-white mb-3">The wedge is access to inventory</h3>
          <p class="text-lg text-slate-400 leading-relaxed">
            Physical-world inventory is invisible until you show up. We surface it before the drive, before the search,
            before the effort. Sellers add items that would otherwise be undiscoverable. Buyers discover and purchase
            what's actually nearby — from our apps, our website, or third-party AI platforms they already use.
          </p>
        </div>

        <div class="glass-card rounded-xl p-6">
          <h3 class="text-xl font-semibold text-white mb-3">AI runs the operation, not a team</h3>
          <p class="text-lg text-slate-400 leading-relaxed">
            Pricing optimization, seller coaching, fraud prevention, demand forecasting, listing quality control, visual
            search indexing, and platform governance are all handled by AI. Buyers and sellers interact through
            autonomous AI agents that execute real ecommerce operations — purchases, listing creation, order management
            — via conversation or voice. The roles that other platforms fill with people, we fill with intelligence.
          </p>
        </div>

        <div class="glass-card rounded-xl p-6">
          <h3 class="text-xl font-semibold text-white mb-3">Data nobody else has</h3>
          <p class="text-lg text-slate-400 leading-relaxed">
            Where physical sellers actually sell, what moves at which locations, foot traffic by time and day,
            cross-seller patterns. Every transaction generates location-commerce intelligence that doesn't exist
            anywhere else and can't be scraped or licensed. It can only be built by operating the platform.
          </p>
        </div>

        <div class="glass-card rounded-xl p-6">
          <h3 class="text-xl font-semibold text-white mb-3">Demand builds across time</h3>
          <p class="text-lg text-slate-400 leading-relaxed">
            Sellers don't reset to zero daily. A buyer browsing Tuesday can order for Saturday pickup. Transaction
            records accumulate. Repeat buyers become customers, not passersby. Commerce persists after the moment.
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- SECTION: THE AGENTIC COMPANY -->
  <section id="workforce" class="scroll-mt-header py-24 lg:py-32 bg-slate-950">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">The Agentic Company</h2>
      <p class="text-lg text-slate-400 leading-relaxed mb-12">
        We didn't adopt AI tools — we built the company around agents. 19 purpose-built agents span 7 departments and
        run across 31 ML models. Every department is operated by intelligence we control. This is what a post-headcount
        company looks like.
      </p>

      <!-- Atlas featured card -->
      <div class="glass-card rounded-xl p-6 border-l-4 border-l-primary-500 mb-10">
        <div class="flex items-start gap-4">
          <div class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
            </svg>
          </div>
          <div class="min-w-0">
            <h3 class="text-lg font-semibold text-white mb-1">Atlas — Command Agent</h3>
            <p class="text-primary-400 text-sm mb-3">Default entry point · Orchestrates all 18 agents</p>
            <p class="text-slate-400 mb-4">
              The orchestrating intelligence. Atlas receives every task, routes to the right agent, spawns parallel
              subagents when needed, and synthesizes results into decisions. One node coordinates all 18 others —
              continuously, without human routing.
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Kernel</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Signal</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Capital</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Hawk</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Helm</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Cortex</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Sage</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Canvas</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Forge</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Sentinel</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Ledger</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Pulse</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Harbor</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Bridge</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Beacon</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Compass</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Prism</span>
              <span class="px-2 py-0.5 rounded border border-white/10 text-slate-400 text-xs">Scholar</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Department grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

        <!-- Engineering -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Engineering</h3>
          </div>
          <p class="text-slate-400 mb-2">Kernel · Forge · Helm</p>
          <p class="text-sm text-slate-500">Platform architecture, build automation, and infrastructure reliability.</p>
        </div>

        <!-- Intelligence -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Intelligence</h3>
          </div>
          <p class="text-slate-400 mb-2">Cortex · Prism · Scholar</p>
          <p class="text-sm text-slate-500">AI/ML research, data science, experimentation, and institutional memory.</p>
        </div>

        <!-- Growth -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Growth</h3>
          </div>
          <p class="text-slate-400 mb-2">Signal · Canvas · Pulse · Beacon</p>
          <p class="text-sm text-slate-500">Marketing, brand, content, and growth analytics.</p>
        </div>

        <!-- Commerce & Finance -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Commerce &amp; Finance</h3>
          </div>
          <p class="text-slate-400 mb-2">Capital · Ledger · Hawk</p>
          <p class="text-sm text-slate-500">Fundraising, financial reporting, sales strategy, and competitive
            intelligence.</p>
        </div>

        <!-- Strategy -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Strategy</h3>
          </div>
          <p class="text-slate-400 mb-2">Sage · Compass · Bridge</p>
          <p class="text-sm text-slate-500">Long-term positioning, product direction, and partnerships.</p>
        </div>

        <!-- Operations & Security -->
        <div
          class="glass-card rounded-xl p-6 group hover:border-primary-500/30 border border-white/5 transition-all duration-300 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/20 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">Operations &amp; Security</h3>
          </div>
          <p class="text-slate-400 mb-2">Harbor · Sentinel</p>
          <p class="text-sm text-slate-500">Logistics, fulfillment, compliance, and platform governance.</p>
        </div>

      </div>

      <!-- Closing statement -->
      <div class="glass-card rounded-xl p-6 border-l-4 border-l-primary-500 mt-8">
        <p class="text-lg text-slate-300 font-medium">
          This isn't a team using AI tools. It's a company architected as an intelligence system — and it runs today, on
          this product, right now.
        </p>
      </div>

    </div>
  </section>

  <!-- SECTION 6: IMAGE SHOWCASE -->
  <section class="py-24 lg:py-32 bg-slate-950 overflow-hidden">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4 text-center">See It in Action</h2>
      <p class="text-lg text-slate-400 text-center mb-16 max-w-2xl mx-auto">
        Built end-to-end. Seller apps, buyer apps, live map, AI intelligence, autonomous agents.
      </p>

      <!-- Showcase cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- IMAGE 1: Buyer Map View -->
        <div class="group">
          <div
            class="aspect-9/16 rounded-2xl bg-slate-800/50 border border-white/5 overflow-hidden relative group-hover:border-primary-500/20 transition-all duration-500 group-hover:-translate-y-2">
            <!-- PLACEHOLDER: Screenshot of the buyer mobile app showing the live map with seller pins.
                 Ideal: Phone mockup of demo.naybrs.com on mobile viewport, map of OC area with colored pins
                 for live sellers, product cards floating near pins, dark UI. -->
            <div class="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-slate-600 mb-4" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p class="text-slate-500 text-sm font-medium">Live Map View</p>
              <p class="text-slate-600 text-xs mt-1">Nearby sellers, real-time</p>
            </div>
            <div class="absolute inset-0 bg-linear-to-t from-slate-900/80 via-transparent to-transparent"></div>
          </div>
          <p class="text-slate-400 text-sm mt-4 text-center">Discover sellers nearby on a live map</p>
        </div>

        <!-- IMAGE 2: Seller Dashboard -->
        <div class="group">
          <div
            class="aspect-9/16 rounded-2xl bg-slate-800/50 border border-white/5 overflow-hidden relative group-hover:border-primary-500/20 transition-all duration-500 group-hover:-translate-y-2">
            <!-- PLACEHOLDER: Screenshot of seller dashboard showing AI intelligence cards.
                 Ideal: Desktop/tablet screenshot of business.naybrs.com dashboard with cards for demand forecast,
                 pricing recommendation, quality score (72/100), location heatmap. Dark UI, data visualizations. -->
            <div class="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-slate-600 mb-4" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p class="text-slate-500 text-sm font-medium">Seller Intelligence</p>
              <p class="text-slate-600 text-xs mt-1">Demand, pricing, quality</p>
            </div>
            <div class="absolute inset-0 bg-linear-to-t from-slate-900/80 via-transparent to-transparent"></div>
          </div>
          <p class="text-slate-400 text-sm mt-4 text-center">AI-powered seller dashboard</p>
        </div>

        <!-- IMAGE 3: Product Detail -->
        <div class="group">
          <div
            class="aspect-9/16 rounded-2xl bg-slate-800/50 border border-white/5 overflow-hidden relative group-hover:border-primary-500/20 transition-all duration-500 group-hover:-translate-y-2">
            <!-- PLACEHOLDER: Screenshot of a product detail page with recommendations.
                 Ideal: Mobile screenshot of a product page (e.g., wildflower honey) showing product photo,
                 price, seller info, "Similar products" carousel, "Bought together" section.
                 Capture from demo.naybrs.com product page. -->
            <div class="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-slate-600 mb-4" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <p class="text-slate-500 text-sm font-medium">Smart Product Page</p>
              <p class="text-slate-600 text-xs mt-1">Recommendations, similar items</p>
            </div>
            <div class="absolute inset-0 bg-linear-to-t from-slate-900/80 via-transparent to-transparent"></div>
          </div>
          <p class="text-slate-400 text-sm mt-4 text-center">Personalized recommendations on every page</p>
        </div>
      </div>
    </div>
  </section>

  <!-- SECTION 7: THE FLYWHEEL -->
  <section id="working" class="scroll-mt-header py-24 lg:py-32 bg-slate-900">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">The Flywheel</h2>
      <p class="text-lg text-slate-400 leading-relaxed mb-12">
        Each participant makes the platform smarter for every other participant.
      </p>

      <!-- Flywheel visualization -->
      <div class="relative py-8">
        <div class="space-y-0">
          <!-- Step 1 -->
          <div class="flex items-start gap-6">
            <div class="flex flex-col items-center">
              <div
                class="w-12 h-12 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center text-primary-400 font-bold shrink-0">
                1</div>
              <div class="w-0.5 h-16 bg-linear-to-b from-primary-500/40 to-primary-500/10"></div>
            </div>
            <div class="pt-2 pb-8">
              <h3 class="text-lg font-semibold text-white mb-2">Sellers list products and attend events</h3>
              <p class="text-slate-400">Each listing and event attendance generates location, pricing, and demand data
                unique to physical commerce.</p>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="flex items-start gap-6">
            <div class="flex flex-col items-center">
              <div
                class="w-12 h-12 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center text-primary-400 font-bold shrink-0">
                2</div>
              <div class="w-0.5 h-16 bg-linear-to-b from-primary-500/40 to-primary-500/10"></div>
            </div>
            <div class="pt-2 pb-8">
              <h3 class="text-lg font-semibold text-white mb-2">Transactions generate proprietary data</h3>
              <p class="text-slate-400">What sold, where, when, for how much, in what conditions. Data that doesn't
                exist anywhere else.</p>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="flex items-start gap-6">
            <div class="flex flex-col items-center">
              <div
                class="w-12 h-12 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center text-primary-400 font-bold shrink-0">
                3</div>
              <div class="w-0.5 h-16 bg-linear-to-b from-primary-500/40 to-primary-500/10"></div>
            </div>
            <div class="pt-2 pb-8">
              <h3 class="text-lg font-semibold text-white mb-2">AI improves for everyone</h3>
              <p class="text-slate-400">Better recommendations for buyers, sharper forecasts for sellers, smarter
                pricing, tighter fraud detection.</p>
            </div>
          </div>

          <!-- Step 4 -->
          <div class="flex items-start gap-6">
            <div class="flex flex-col items-center">
              <div
                class="w-12 h-12 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center text-primary-400 font-bold shrink-0">
                4</div>
              <div class="w-0.5 h-16 bg-linear-to-b from-primary-500/40 to-transparent"></div>
            </div>
            <div class="pt-2 pb-8">
              <h3 class="text-lg font-semibold text-white mb-2">More value attracts more participants</h3>
              <p class="text-slate-400">Buyers find better products. Sellers make more money. Both bring others. The
                cycle accelerates.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="glass-card rounded-xl p-6 border-l-4 border-l-primary-500">
        <p class="text-lg text-slate-300 font-medium">
          The data moat deepens with every transaction. A competitor who starts tomorrow is permanently behind on the
          intelligence that makes the platform work.
        </p>
      </div>
    </div>
  </section>

  <!-- SECTION 8: WHY THIS MATTERS -->
  <section id="why" class="scroll-mt-header py-24 lg:py-32 bg-slate-950">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl sm:text-4xl font-bold text-white mb-8">Why This Matters Beyond Commerce</h2>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">
        Most physical sellers never become businesses. They sell at a few events, don't build sustained demand, and
        eventually
        stop. The gap isn't product quality or pricing -- it's discovery and persistence.
      </p>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">
        Large marketplaces don't solve this because they optimize for scale, not proximity. Delivery platforms don't
        solve it
        because they assume established inventory. No existing platform makes informal, physical-world selling viable as
        a long-term business.
      </p>
      <p class="text-lg text-slate-400 leading-relaxed mb-6">
        We're building infrastructure that makes it trivial to sell nearby. Not through subsidies, not through
        logistics, but through access and intelligence.
      </p>
      <div class="glass-card rounded-xl p-6 border-l-4 border-l-primary-500">
        <p class="text-lg text-slate-300 font-medium mb-3">
          If this works, the default for buying things shifts.
        </p>
        <p class="text-slate-400">
          Instead of searching Amazon first, buyers check what's nearby. Instead of browsing blindly, they see inventory
          before they drive. Instead of defaulting to large platforms, they transact with sellers they can meet.
        </p>
      </div>
    </div>
  </section>

  <!-- SECTION 9: THE FOUNDER -->
  <section id="founder" class="scroll-mt-header py-24 lg:py-32 bg-slate-900">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="glass-card rounded-2xl border border-white/5 p-6 sm:p-8 lg:p-10 overflow-hidden">
        <div class="flex flex-col gap-8 md:flex-row md:items-center">
          <div class="shrink-0">
            <img src="assets/images/founder-image.png" alt="Adam Paul Wright, founder of Naybrs"
              class="h-40 w-40 rounded-2xl object-cover border border-white/10 shadow-2xl shadow-slate-950/40" />
          </div>

          <div class="min-w-0 flex-1">
            <p class="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-primary-400">Founder</p>
            <h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">Adam Paul Wright</h2>
            <p class="text-lg text-slate-300 leading-relaxed mb-4">
              Adam is the founder of Naybrs and a solo technical builder. He has built the platform end-to-end across
              the apps, infrastructure, and commerce systems, with a focus on making physical-world selling easier
              to discover and buy from.
            </p>


            <div class="flex flex-col sm:flex-row gap-3">
              <a href="https://www.linkedin.com/in/adampaulwright/" target="_blank" rel="noopener"
                class="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-all duration-300 shadow-lg shadow-primary-500/20 hover:-translate-y-0.5">
                LinkedIn
              </a>
              <a href="mailto:a@naybrs.com"
                class="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-slate-300 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-xl transition-all duration-300">
                a@naybrs.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- FOOTER -->
  <footer id="footer" class="py-16 bg-slate-950 border-t border-white/5">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <p class="text-xl font-semibold text-white mb-2">Naybrs, Inc.</p>
        <p class="text-sm text-slate-500 mb-6">Member of the NVIDIA Inception program</p>
        <div class="flex justify-center mb-6">
          <img src="assets/images/partners/nvidia-inception-badge.svg" alt="NVIDIA Inception Program member badge"
            class="h-12 opacity-60" />
        </div>
        <div class="space-y-2">
          <p>
            <a href="mailto:a@naybrs.com" class="text-slate-400 hover:text-primary-400 transition-colors">
              a&#64;naybrs.com
            </a>
          </p>
          <p>
            <a href="https://naybrs.com" target="_blank" rel="noopener"
              class="text-slate-400 hover:text-primary-400 transition-colors">
              naybrs.com
            </a>
          </p>
        </div>
      </div>

      <div class="text-center pt-8 border-t border-white/5">
        <button (click)="scrollToTop()"
          class="inline-flex items-center gap-2 text-slate-500 hover:text-slate-300 transition-colors mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          Back to top
        </button>
        <p class="text-sm text-slate-600">
          &copy; {{ currentYear }} Naybrs, Inc. All rights reserved.
          <span class="mx-2">&middot;</span>
          <a routerLink="/p/privacy-policy" class="hover:text-slate-400 transition-colors">Privacy Policy</a>
          <span class="mx-2">&middot;</span>
          <a routerLink="/p/terms-and-conditions" class="hover:text-slate-400 transition-colors">Terms & Conditions</a>
        </p>
      </div>
    </div>
  </footer>
</div>